import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Activity, Building2, LayoutDashboard, LogOut, Menu, ShieldCheck, Users, X } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../../lib/auth'
import { initials } from '../../lib/format'
import { IconButton } from '../ui/Button'

/**
 * App shell — the same sidebar/topbar structure as the tenant app's own
 * AppShell (fixed sidebar on desktop, slide-over drawer on mobile, a bottom
 * user panel with sign-out), rebuilt rather than shared because it has
 * nothing to brand: no tenant logo, no permission-built navigation, no
 * committee membership. Two links, plainly labelled, is the whole nav
 * surface — adding the tenant shell's generality here would be complexity
 * with nothing to apply it to.
 */
export default function Shell() {
  const { admin, signOut } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const links = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/tenants', label: 'Tenants', icon: Building2 },
    { to: '/activity', label: 'Activity', icon: Activity },
    { to: '/admins', label: 'Platform admins', icon: Users },
  ]

  return (
    <div className="h-dvh overflow-hidden bg-[var(--color-surface-sunken)]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-[var(--color-line)] bg-[var(--color-surface)] lg:flex">
        <Brand />
        <Nav links={links} onNavigate={() => setDrawerOpen(false)} />
        <UserPanel admin={admin} onSignOut={signOut} />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="relative flex h-full w-[17rem] flex-col border-r border-[var(--color-line)] bg-[var(--color-surface)]"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div className="flex items-center justify-between border-b border-[var(--color-line)] pr-2">
              <Brand bordered={false} />
              <IconButton icon={X} label="Close navigation" onClick={() => setDrawerOpen(false)} />
            </div>
            <Nav links={links} onNavigate={() => setDrawerOpen(false)} />
            <UserPanel admin={admin} onSignOut={signOut} />
          </aside>
        </div>
      )}

      <div className="flex h-full flex-col lg:pl-60">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--color-line)] bg-[var(--color-surface)] px-4 lg:hidden">
          <IconButton icon={Menu} label="Open navigation" onClick={() => setDrawerOpen(true)} />
          <span className="font-display text-sm font-semibold text-[var(--color-ink-900)]">
            Platform administration
          </span>
        </header>

        <main id="main" className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function Brand({ bordered = true }) {
  return (
    <div className={clsx('flex h-14 items-center gap-2 px-4', bordered && 'border-b border-[var(--color-line)]')}>
      <ShieldCheck size={20} style={{ color: 'var(--color-accent)' }} aria-hidden="true" />
      <span className="truncate font-display text-sm font-semibold text-[var(--color-ink-900)]">
        Platform admin
      </span>
    </div>
  )
}

function Nav({ links, onNavigate }) {
  return (
    <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 py-3">
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors duration-150',
              isActive
                ? 'bg-[var(--color-accent-soft)] font-medium text-[var(--color-accent-text)]'
                : 'text-[var(--color-ink-700)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink-900)]',
            )
          }
        >
          <link.icon size={16} className="shrink-0" aria-hidden="true" />
          <span className="truncate">{link.label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

function UserPanel({ admin, onSignOut }) {
  return (
    <div className="border-t border-[var(--color-line)] p-2.5">
      <div className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-xs font-semibold text-[var(--color-accent-text)]"
          aria-hidden="true"
        >
          {initials(admin?.name)}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-[var(--color-ink-900)]">
            {admin?.name}
          </span>
          <span className="block truncate text-xs text-[var(--color-ink-500)]">
            {admin?.email}
          </span>
        </span>

        <IconButton icon={LogOut} label="Sign out" onClick={onSignOut} />
      </div>
    </div>
  )
}
